import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { firestore } from '../../firebase/config';
import { collection, addDoc } from 'firebase/firestore';

const BusinessComingSoon = () => {
  const [email, setEmail] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Add to waitlist in Firestore
      await addDoc(collection(firestore, 'businessWaitlist'), {
        email: email,
        businessName: businessName,
        submittedAt: new Date(),
        status: 'pending'
      });

      setSubmitted(true);
      console.log('Business added to waitlist:', email);
    } catch (error) {
      console.error('Error adding to waitlist:', error);
      setError('Failed to join waitlist. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate('/');
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-xl overflow-hidden">
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">You're on the list!</h2>
            <p className="text-gray-600 mb-6">
              Thank you for your interest! We'll notify you as soon as our business portal is ready.
            </p>
            
            <div className="space-y-3">
              <button
                onClick={handleGoBack}
                className="w-full py-3 px-4 bg-gradient-to-r from-orange-400 to-orange-500 text-white rounded-md hover:from-orange-500 hover:to-orange-600 transition-colors"
              >
                Back to Homepage
              </button>
              
              <Link
                to="/login"
                className="block w-full py-3 px-4 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-center"
              >
                Job Seeker? Sign In Here
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center p-4">
      {/* Background pattern */}
      <div className="absolute inset-0 grid grid-cols-4 grid-rows-3 gap-4 z-0">
        {Array(12).fill().map((_, i) => (
          <div key={i} className="rounded-lg bg-gradient-to-br from-orange-200 to-orange-100 opacity-50"></div>
        ))}
      </div>

      <div className="relative z-10 max-w-4xl w-full bg-white rounded-lg shadow-xl overflow-hidden">
        <div className="md:flex">
          {/* Content Side */}
          <div className="w-full md:w-1/2 p-8 md:p-12">
            <div className="flex items-center mb-8">
              <img 
                src="/asset/Logo/hireeLogo.png" 
                alt="Hiree Logo" 
                className="h-10 w-auto"
              />
              <span className="ml-3 text-xl text-gray-600">for Businesses</span>
            </div>

            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Business Portal
                <span className="block text-2xl text-orange-500 mt-2">Coming Soon!</span>
              </h1>
              
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                We're working hard to create an amazing hiring platform for businesses like yours. 
                Join our waitlist to be the first to know when we launch!
              </p>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-orange-800 mb-2">What to expect:</h3>
                <ul className="text-orange-700 space-y-1 text-sm">
                  <li>• Post job openings instantly</li>
                  <li>• Manage applications efficiently</li>
                  <li>• Connect with qualified candidates</li>
                  <li>• Advanced business analytics</li>
                  <li>• Dedicated business support</li>
                </ul>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleWaitlistSubmit} className="space-y-4">
              <div>
                <label htmlFor="businessName" className="block text-left text-gray-700 font-medium mb-2">
                  Business Name<span className="text-orange-500">*</span>
                </label>
                <input
                  id="businessName"
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="Enter your business name"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-left text-gray-700 font-medium mb-2">
                  Email Address<span className="text-orange-500">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your business email"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-gradient-to-r from-orange-400 to-orange-500 text-white rounded-md hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-opacity-50 disabled:opacity-70 font-medium"
              >
                {loading ? 'Joining Waitlist...' : 'Join the Waitlist'}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-center text-sm text-gray-600">
                Looking for a job instead?{' '}
                <Link to="/signup/regular" className="text-orange-500 hover:text-orange-600 font-medium">
                  Create Job Seeker Account
                </Link>
              </p>
              
              <div className="mt-3 text-center">
                <button
                  onClick={handleGoBack}
                  className="text-gray-500 hover:text-gray-700 text-sm"
                >
                  ← Back to Homepage
                </button>
              </div>
            </div>
          </div>

          {/* Visual Side */}
          <div className="hidden md:block w-1/2 bg-gradient-to-br from-orange-400 to-orange-600 p-12 text-white relative overflow-hidden">
            <button
              onClick={handleGoBack}
              className="absolute top-4 right-4 p-2 bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="flex flex-col h-full justify-center">
              <div className="mb-8">
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mb-6">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                
                <h2 className="text-3xl font-bold mb-4">
                  Build Your Team
                </h2>
                <p className="text-lg opacity-90 mb-6">
                  Our business portal will help you find and hire the best talent for your restaurant and service business.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-white bg-opacity-20 rounded-full flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 m-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold">Smart Candidate Matching</h4>
                    <p className="text-sm opacity-80">AI-powered recommendations based on your needs</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-white bg-opacity-20 rounded-full flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 m-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold">Streamlined Hiring Process</h4>
                    <p className="text-sm opacity-80">From posting to hiring in just a few clicks</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-white bg-opacity-20 rounded-full flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 m-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold">Business Dashboard</h4>
                    <p className="text-sm opacity-80">Track applications and manage your hiring pipeline</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-white border-opacity-20">
                <p className="text-sm opacity-75">
                  Join <span className="font-bold">500+</span> businesses already on our waitlist
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessComingSoon; 